import axios from "axios";
import { type ClassValue, clsx } from "clsx";
import { UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { logOnDev } from "@/services/axios/interceptors/response";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const stringToColour = function (str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  const saturation = 70;
  const lightness = 70;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

type NestedObject = Record<string, TODO>;

export function getElementByStringPath(
  obj: NestedObject,
  path: string
): unknown {
  const keys = path.split(".");
  let current = obj;

  for (const key of keys) {
    if (Object.hasOwn(current, key)) {
      current = current[key];
    } else {
      return undefined; // Key not found
    }
  }

  return current;
}

export const isNumbers = (str: string) => /^\d*$/.test(str);

export const _handleFormError = (form: UseFormReturn, error: unknown) => {
  // Not an axios error
  if (!axios.isAxiosError(error)) {
    form.setError("root", {
      message: "An error occured, please try again later.",
    });
    return;
  }
  if (!error.response) {
    form.setError("root", {
      message: "An error occured, please try again later.",
    });
    return;
  }
  const errorData = error.response.data;

  logOnDev("errorData:", errorData);

  if (Array.isArray(errorData)) {
    form.setError("root", { message: errorData.join("\n") });
  } else if (typeof errorData === "object" && "error" in errorData) {
    form.setError("root", { message: errorData.error });
  } else if (typeof errorData === "object") {
    for (const fieldKey in errorData) {
      logOnDev("setting error for", fieldKey);

      if (Object.hasOwn(errorData, fieldKey)) {
        const errorMessage = errorData[fieldKey];
        form.setError(fieldKey, {
          message: Array.isArray(errorMessage)
            ? errorMessage.join("\n")
            : errorMessage,
        });
      }
    }
  } else {
    form.setError("root", {
      message: "An error occured, please try again later.",
    });
  }
};

const UNEXPECTED_FORM_ERROR_MESSAGE =
  "An unexpected error occurred. Please try again.";

/**
 * Handles form errors by setting form error messages based on Axios error responses.
 * @param {UseFormReturn} form - The form instance from react-hook-form.
 * @param {unknown} error - The error object, potentially an AxiosError.
 */
export const handleFormError = (form: UseFormReturn<TODO>, error: unknown) => {
  const formValues = form.getValues();

  if (!axios.isAxiosError(error)) {
    form.setError("root", { message: UNEXPECTED_FORM_ERROR_MESSAGE });
    return;
  }

  const { response } = error;

  if (!response) {
    form.setError("root", {
      message:
        "Network error. Please check your internet connection and try again.",
    });
    return;
  }

  const { data, status } = response;

  if (status === 401) {
    form.setError("root", {
      message: "Unauthorized access. Please log in again.",
    });
    return;
  }

  if (Array.isArray(data)) {
    form.setError("root", { message: data.join("\n") });
    return;
  }

  if (typeof data === "object" && "error" in data) {
    form.setError("root", { message: data.error });
    return;
  }

  if (typeof data === "object") {
    for (const fieldKey in data) {
      const errorMessage = data[fieldKey];
      const message = Array.isArray(errorMessage)
        ? errorMessage.join("\n")
        : errorMessage;

      if (fieldKey in formValues) {
        logOnDev("setting error for", fieldKey, true);
        form.setError(fieldKey, { message });
      } else {
        form.setError("root", { message: UNEXPECTED_FORM_ERROR_MESSAGE });
      }
    }
    return;
  }

  form.setError("root", { message: UNEXPECTED_FORM_ERROR_MESSAGE });
};

// Function to get the dates for the current week
export const getWeekDates = () => {
  const weekDates = [];
  const date = new Date();
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday

  for (let i = 0; i < 7; i++) {
    const newDate = new Date(date.setDate(diff + i));
    weekDates.push(
      newDate.toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "short",
      })
    );
  }
  return weekDates;
};

export const groupBy = <T, K extends keyof unknown>(
  list: T[],
  getKey: (item: T) => K
) =>
  list.reduce(
    (previous, currentItem) => {
      const group = getKey(currentItem);
      if (!previous[group]) previous[group] = [];
      previous[group].push(currentItem);
      return previous;
    },
    {} as Record<K, T[]>
  );
